// GA4 analytics layer + first-party funnel beacon. Safe no-ops if unavailable.
// Standardized event names (snake_case) + common params auto-merged on every event.
import { getAttribution, isApprovedHost } from "./attribution";
import { getVisitorId, getSessionId, isReturning, initSession } from "./session";

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const EVENTS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/events`;

// Event names mirrored into our own DB for the Founder Funnel (owned, live data).
const FUNNEL_EVENTS = new Set([
  "visit", "page_view", "search_colleges_click", "search_performed", "college_view",
  "compare_colleges_click", "talk_to_ai_click", "ai_guidance_click", "admission_guidance_click",
  "ai_chat_started", "form_start", "ai_step_1_completed", "ai_step_2_completed",
  "ai_step_3_completed", "recommendation_generated", "lead_generated", "lead_submitted", "form_submit",
  "whatsapp_click", "expert_counselling_click", "expert_call_clicked", "begin_checkout", "purchase", "payment_failed",
  "sort_changed", "pagination", "filter_applied", "filter_cleared",
  "cta_impression", "cta_click", "exit_intent_shown",
]);

function ready() {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

const isAdminPath = (p) => ((p ?? window.location.pathname) || "").startsWith("/admin");

// Revenue & CRM lifecycle events are SERVER-AUTHORITATIVE (GA4 Measurement Protocol).
// They must never be sent to GA from the browser; internal funnel beacon still records them.
const SERVER_ONLY_GA_EVENTS = new Set([
  "purchase", "refund", "qualify_lead", "disqualify_lead",
  "working_lead", "close_convert_lead", "close_unconvert_lead",
]);

// Tag-level kill switch: blocks ALL GA network hits (incl. enhanced measurement)
// on non-production hosts and on /admin routes.
export function applyGaGate(path) {
  try { window[`ga-disable-${GA_ID}`] = !isApprovedHost() || isAdminPath(path); } catch {}
}

function commonParams() {
  try {
    const a = getAttribution();
    return clean({
      page_location: window.location.href,
      page_path: window.location.pathname,
      traffic_source: a.trafficSource,
      device: a.device,
      // GA4-recommended UTM fields so campaign reports work for conversion events.
      utm_source: a.utmSource,
      utm_medium: a.utmMedium,
      utm_campaign: a.utmCampaign,
      utm_content: a.utmContent,
      utm_term: a.utmTerm,
      gclid: a.gclid,
    });
  } catch {
    return {};
  }
}

// Mirror a funnel event into our first-party store (fire-and-forget, never throws).
function beacon(name, params = {}) {
  try {
    if (!FUNNEL_EVENTS.has(name) || !process.env.REACT_APP_BACKEND_URL) return;
    const a = getAttribution();
    const payload = {
      events: [{
        name,
        sessionId: getSessionId(),
        visitorId: getVisitorId(),
        returning: isReturning(),
        device: a.device || "",
        source: a.trafficSource || "direct",
        path: window.location.pathname || "",
        referrer: a.referrer || "",
        state: params.state || null,
        city: params.city || null,
        course: params.course || params.courseInterest || null,
        collegeId: params.college_id || params.collegeId || null,
        collegeSlug: params.slug || params.college_slug || params.collegeSlug || null,
        plan: params.plan || params.plan_code || null,
        value: typeof params.value === "number" ? params.value : null,
        variant: params.variant || null,
        ctaId: params.cta_id || null,
      }],
    };
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(EVENTS_URL, new Blob([body], { type: "application/json" }));
    } else {
      fetch(EVENTS_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch {}
}

// Drop null/undefined/empty-string params so GA reports stay clean.
function clean(params = {}) {
  const out = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") out[k] = v;
  });
  return out;
}

let _lastPageView = null;
let _sessionStarted = false;

export function gaPageView(path) {
  // Establish first-party session + a one-time "visit" on first page.
  try {
    initSession();
    if (!_sessionStarted) {
      _sessionStarted = true;
      beacon("visit", {});
    }
  } catch {}
  if (!ready() || !GA_ID) return;
  if (isAdminPath(path)) return; // admin routes never enter public acquisition reporting
  if (_lastPageView === path) return; // de-dupe rapid double route fires
  _lastPageView = path;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

// GA4-recommended event-name aliases. When you `track("search_performed", ...)`
// we still record "search_performed" in our first-party funnel, but gtag receives
// GA4 event aliasing: some internal event names use CU domain vocabulary; when they
// map cleanly onto a GA4-recommended event, we send the GA4-recommended name so the
// Search-terms / Item-views reports work out of the box. Also used to standardise
// conversion-event names against the current analytics spec (whatsapp_clicked,
// ai_started, recommendations_viewed) without touching every callsite.
const GA4_ALIAS = {
  search_performed: "search",
  college_view: "view_item",
  whatsapp_click: "whatsapp_clicked",
  ai_chat_started: "ai_started",
  recommendation_generated: "recommendations_viewed",
};

// Primary event API — mirrors to first-party store + GA4.
export function track(name, params = {}) {
  beacon(name, params);
  if (!ready()) return;
  if (SERVER_ONLY_GA_EVENTS.has(name)) return; // server-side only (Measurement Protocol)
  if (isAdminPath()) return;
  const gaName = GA4_ALIAS[name] || name;
  window.gtag("event", gaName, { ...commonParams(), ...clean(params) });
}

// GA4 recommended lead event — fires exactly once per backend-CONFIRMED new lead.
// `lead` is the API response ({ id, source, isNewLead }); lead_id is the non-PII dedupe key.
// `params.lead_type` and `params.form_name` are the required GA4 report dimensions
// (e.g. "admission_guidance_form" / "Admission Guidance"). No PII is ever sent.
export function trackLeadGenerated(lead, params = {}) {
  try {
    if (!lead || !lead.id || lead.isNewLead === false) return; // duplicates never convert
    const sent = JSON.parse(localStorage.getItem("cu_gl_sent") || "[]");
    if (sent.includes(lead.id)) return;
    localStorage.setItem("cu_gl_sent", JSON.stringify([...sent.slice(-49), lead.id]));
    track("lead_generated", { ...params, lead_source: lead.source }); // internal funnel event
    if (ready() && !isAdminPath()) {
      window.gtag("event", "generate_lead", {
        ...commonParams(),
        ...clean({
          lead_id: lead.id,
          lead_source: lead.source || params.lead_source || params.intent,
          lead_type: params.lead_type,
          form_name: params.form_name,
        }),
      });
    }
  } catch {}
}

// GA4 "book_counselling" — canonical booking event. Fires ONCE per backend-confirmed
// booking (i.e. after /leads/expert-counseling has returned a lead id, or after a
// successful paid-checkout success screen). Idempotent across double-clicks, re-renders
// and route changes via localStorage. Not tied to any CTA click.
export function trackBookCounselling(bookingKey, params = {}) {
  try {
    if (!bookingKey) return;
    const sent = JSON.parse(localStorage.getItem("cu_bc_sent") || "[]");
    if (sent.includes(bookingKey)) return;
    localStorage.setItem("cu_bc_sent", JSON.stringify([...sent.slice(-49), bookingKey]));
    if (ready() && !isAdminPath()) {
      window.gtag("event", "book_counselling", {
        ...commonParams(),
        ...clean({
          booking_type: params.booking_type || "expert_counselling",
          booking_id: bookingKey,
        }),
      });
    }
  } catch {}
}

// Backward-compatible alias (older call sites).
export function gaEvent(name, params = {}) {
  track(name, params);
}

